<template>
  <ModalComponent
    :submitButtonText="$t('forwards.deals.producer_prices.submit')"
    :hideSubmitButton="true"
    size="lg"
    @close="close"
    @setForceToggle="setForceToggle"
  >
    <template #title>{{ title }}</template>

    <template #body>
      <div
        v-if="mergeNotice"
        class="alert alert-warning py-2 mb-3"
        role="status"
        data-testid="prices-merge-notice"
      >
        <template v-if="mergeNotice.shipped">
          {{ $t("forwards.deals.producer_prices.shipped_meanwhile") }}
        </template>
        <template v-else-if="delivery === null">
          {{ $t("forwards.deals.producer_prices.delivery_gone") }}
        </template>
        <template v-else>
          <p v-if="mergeUnaffected" class="mb-0">
            {{ $t("forwards.deals.merge.unaffected") }}
          </p>
          <p v-else class="mb-1">
            {{ $t("forwards.deals.merge.changed_meanwhile") }}
          </p>
          <ul v-if="!mergeUnaffected" class="mb-0 ps-3">
            <li v-if="mergeNotice.takenOver > 0">
              {{ $t("forwards.deals.merge.taken_over", mergeNotice.takenOver) }}
            </li>
            <li v-if="mergeNotice.conflicts > 0">
              {{ $t("forwards.deals.merge.conflicts", mergeNotice.conflicts) }}
            </li>
            <li v-if="mergeNotice.mandatesRemoved > 0">
              {{
                $t(
                  "forwards.deals.producer_prices.mandates_removed",
                  mergeNotice.mandatesRemoved
                )
              }}
            </li>
          </ul>
        </template>
      </div>

      <div v-if="delivery" class="p-1">
        <p class="text-body-tertiary">
          {{ $t("forwards.deals.producer_prices.body") }}
        </p>

        <div
          v-for="conflict in priceConflicts"
          :key="conflict.company_id"
          :class="[
            'alert py-2 mb-3',
            conflict.holds_shipment ? 'alert-danger' : 'alert-warning',
          ]"
          role="alert"
          data-testid="price-conflict"
        >
          {{
            $t(
              conflict.holds_shipment
                ? "forwards.deals.producer_prices.conflict"
                : "forwards.deals.producer_prices.foreseen_conflict",
              {
                company: conflict.company_name,
                mandates: conflictText(conflict),
              }
            )
          }}
        </div>

        <div class="mb-3">
          <label class="form-label" :for="defaultPriceId">
            {{ $t("forwards.deals.producer_prices.default_price") }}
          </label>
          <p v-if="isReadOnly" class="mb-0" data-testid="default-price">
            {{ readOnlyDefaultPrice }}
          </p>
          <DraftConflictComponent
            v-else-if="mergeConflicts[DEFAULT_PRICE_FIELD]"
            :mine="defaultPriceText(mergeConflicts[DEFAULT_PRICE_FIELD].mine)"
            :theirs="
              defaultPriceText(mergeConflicts[DEFAULT_PRICE_FIELD].theirs)
            "
            @pick="pick(DEFAULT_PRICE_FIELD, $event)"
          />
          <input
            v-else
            :id="defaultPriceId"
            v-model="draft[DEFAULT_PRICE_FIELD]"
            type="number"
            step="0.001"
            class="form-control form-control-sm default-price-input"
            :placeholder="delivery.unit_price ?? ''"
            data-testid="default-price-input"
          />
          <div class="form-text">
            {{ $t("forwards.deals.producer_prices.default_price_help") }}
          </div>
        </div>

        <p v-if="rows.length === 0" class="text-body-tertiary fst-italic mb-0">
          {{ $t("forwards.deals.producer_prices.no_mandates") }}
        </p>

        <table v-else class="table table-sm mb-0">
          <thead>
            <tr>
              <th scope="col">
                {{ $t("forwards.deals.producer_prices.mandate") }}
              </th>
              <th scope="col" class="text-end">
                {{ $t("forwards.deals.producer_prices.amount") }}
              </th>
              <th scope="col" class="price-cell">
                {{ $t("forwards.deals.producer_prices.price") }}
              </th>
            </tr>
          </thead>
          <tbody v-for="row in rows" :key="row.mandateId">
            <tr data-testid="mandate-row">
              <td>
                <span class="fw-semibold">{{ row.mandateName }}</span>
                <span
                  v-if="!row.backsDelivery"
                  class="badge text-bg-warning ms-1"
                  :title="$t('forwards.deals.producer_prices.kept_price_help')"
                >
                  {{ $t("forwards.deals.producer_prices.kept_price") }}
                </span>
                <div
                  v-if="row.minimumPrice !== null"
                  class="text-body-tertiary fs-10"
                >
                  {{
                    $t("forwards.deals.producer_prices.mandate_floor", {
                      price: row.minimumPrice,
                    })
                  }}
                </div>
              </td>
              <td class="text-end">
                <template v-if="row.backsDelivery">
                  {{ formatNumberWithSpaces(row.amount) }}
                </template>
              </td>
              <td class="price-cell">
                <span v-if="isReadOnly">{{ row.savedPrice ?? "" }}</span>
                <DraftConflictComponent
                  v-else-if="mergeConflicts[row.field]"
                  :mine="priceText(mergeConflicts[row.field].mine)"
                  :theirs="priceText(mergeConflicts[row.field].theirs)"
                  @pick="pick(row.field, $event)"
                />
                <div v-else class="d-flex align-items-center gap-1">
                  <input
                    v-model="draft[row.field]"
                    type="number"
                    step="0.001"
                    class="form-control form-control-sm"
                    :placeholder="fallbackPrice ?? ''"
                    :aria-label="
                      $t('forwards.deals.producer_prices.price_for', {
                        mandate: row.mandateName,
                      })
                    "
                  />
                  <button
                    v-if="!row.backsDelivery"
                    type="button"
                    class="btn btn-sm btn-link text-secondary px-1"
                    :aria-label="
                      $t(
                        'forwards.deals.producer_prices.remove_mandate_price',
                        {
                          mandate: row.mandateName,
                        }
                      )
                    "
                    data-testid="remove-mandate-price"
                    @click="removeMandatePrice(row.field)"
                  >
                    <FontAwesomeIcon icon="fa-solid fa-xmark" />
                  </button>
                </div>
              </td>
            </tr>
            <tr
              v-for="line in row.lines"
              :key="line.companyId"
              data-testid="line-row"
            >
              <td class="ps-4">
                {{ line.companyName }}
                <span
                  v-if="line.inConflict"
                  class="badge text-bg-danger ms-1"
                  data-testid="line-conflict"
                >
                  {{ $t("forwards.deals.producer_prices.conflict_badge") }}
                </span>
              </td>
              <td class="text-end">
                {{ formatNumberWithSpaces(line.amount) }}
              </td>
              <td class="price-cell text-body-tertiary">
                {{ line.invoicedAt }}
              </td>
            </tr>
            <tr v-if="row.backsDelivery && row.lines.length === 0">
              <td colspan="3" class="ps-4 text-body-tertiary fst-italic">
                {{ $t("forwards.deals.producer_prices.no_escrow_yet") }}
              </td>
            </tr>
          </tbody>
        </table>

        <p v-if="rows.length > 0" class="text-body-tertiary fs-10 mt-2 mb-0">
          {{ deliveryPriceNote }}
        </p>

        <div class="mt-3">
          <div class="form-label">
            {{ $t("forwards.deals.producer_prices.trade_owner") }}
          </div>
          <p v-if="isReadOnly" class="mb-0" data-testid="trade-owner">
            {{ readOnlyTradeOwner }}
          </p>
          <DraftConflictComponent
            v-else-if="mergeConflicts[TRADE_OWNER_FIELD]"
            :mine="tradeOwnerText(mergeConflicts[TRADE_OWNER_FIELD].mine)"
            :theirs="tradeOwnerText(mergeConflicts[TRADE_OWNER_FIELD].theirs)"
            @pick="pick(TRADE_OWNER_FIELD, $event)"
          />
          <SearchableSelectComponent
            v-else
            v-model="tradeOwnerCompanyId"
            :options="tradeOwnerOptions"
            :placeholder="
              $t('forwards.deals.producer_prices.trade_owner_placeholder')
            "
          />
          <div class="form-text">
            {{ $t("forwards.deals.producer_prices.trade_owner_help") }}
          </div>
        </div>

        <p v-if="errorMessage" class="error-note mt-2 mb-0" role="alert">
          <FontAwesomeIcon icon="fa-solid fa-exclamation-triangle" />
          <span>{{ errorMessage }}</span>
        </p>
      </div>
    </template>

    <template #footer>
      <button type="button" class="btn btn-link text-secondary" @click="close">
        {{
          isReadOnly
            ? $t("forwards.deals.common.close")
            : $t("forwards.deals.common.cancel")
        }}
      </button>
      <ActionButtonWithSpinner
        v-if="!isReadOnly"
        :busy="busy"
        :disabled="busy || hasMergeConflicts"
        variant="btn-primary"
        @click="submit"
      >
        {{ $t("forwards.deals.producer_prices.submit") }}
      </ActionButtonWithSpinner>
    </template>
  </ModalComponent>
</template>

<script setup lang="ts">
import ActionButtonWithSpinner from "@/components/common/ActionButtonWithSpinner.vue";
import ModalComponent from "@/components/common/ModalComponent.vue";
import SearchableSelectComponent from "@/components/common/SearchableSelectComponent.vue";
import { formatNumberWithSpaces } from "@/composables/formatUtils";
import { useSwapReferrers } from "@/composables/useSwapReferrers";
import DraftConflictComponent from "@/forwards2/deals/DraftConflictComponent.vue";
import type { DraftConflict } from "@/forwards2/deals/draftMerge";
import {
  DEFAULT_PRICE_FIELD,
  draftPriceConflicts,
  mandateField,
  mergeProducerPrices,
  type ProducerPricesDraft,
  type ProducerPriceValue,
  producerPricesRequest,
  savedProducerPrices,
  TRADE_OWNER_FIELD,
  valueText,
} from "@/forwards2/deals/producerPricesDraft";
import { useDeliveryWindowText } from "@/forwards2/deals/useDeliveryWindowText";
import { usePriceConflictText } from "@/forwards2/deals/usePriceConflictText";
import type {
  DeliveryResponse,
  PriceConflict,
  ProducerPricesRequest,
} from "@/forwards2/forwardsTypes";
import { omit, sumBy } from "lodash-es";
import { computed, ref, useId, watch } from "vue";
import { useI18n } from "vue-i18n";

/**
 * One producer line under the mandate whose price it carries.
 *
 * @property invoicedAt - What the line invoices at now, as saved.
 * @property inConflict - The line's escrow came through differently priced mandates of this delivery,
 *   which holds the shipment.
 */
interface LineRow {
  companyId: number;
  companyName: string;
  amount: number;
  invoicedAt: string;
  inConflict: boolean;
}

/**
 * One mandate as the dialog lists it; its price lives in the draft under `field`.
 *
 * @property backsDelivery - False for a price kept after the mandate stopped backing the delivery.
 * @property amount - The volume of the lines under it.
 */
interface MandateRow {
  mandateId: number;
  mandateName: string;
  field: string;
  savedPrice: string | null;
  minimumPrice: string | null;
  backsDelivery: boolean;
  amount: number;
  lines: LineRow[];
}

/** What the dialog says after a 409 merge, until it closes. */
interface MergeNotice {
  takenOver: number;
  conflicts: number;
  mandatesRemoved: number;
  shipped: boolean;
}

/**
 * What each mandate's producers selling into one delivery are paid — staff-only (spec, UI -> Gating
 * and visibility): a super admin sets the prices here, and a sales admin reads them (`readOnly`, user
 * decision 2026-09-23). Prices are per mandate (user, 2026-10-22): a producer is paid the price of
 * the mandate its escrow came through, so the dialog lists each backing mandate with the producer
 * lines under it.
 *
 * The form states the delivery's prices as a whole: every backing mandate is on screen, so a row left
 * blank means "pay this mandate the default producer price" — or the delivery's own price when there
 * is no default — rather than "leave whatever was there"; a typed price is the mandate's own even
 * when it equals the default, and stays put when the default moves. The mandates are known from the
 * plan, so they can be priced before any escrow arrives. A price kept for a mandate that stopped
 * backing the delivery is listed as such: resent with the rest it stays, and applies again if the
 * mandate backs the delivery again; removed, it is gone. The floors are the backend's to enforce; its
 * refusal names the mandate and the figure.
 *
 * A line whose escrow came through two differently priced mandates of the delivery is a price
 * conflict: it cannot carry both prices, so the delivery cannot ship until the mandates are priced alike or
 * their slices are separated. A company that is also a member of a differently priced backing mandate
 * is a foreseen conflict: nothing is held back yet, but allocation may bring its escrow through both.
 * The dialog judges conflicts from the prices as typed, so it names the ones a save would leave, the
 * ones already saved included, and flags the lines whose conflict holds the shipment.
 *
 * The trade owner — paid commission on the swap margin — is saved with the prices and stated whole
 * too: a cleared select clears it, and it can be set before any escrow (user decision 2026-09-28).
 *
 * The save carries the `plan_version` the dialog was opened with. When someone else saved
 * meanwhile, the parent reloads the deal and calls `mergeSavedPrices`: the draft is kept and merged
 * per mandate and for the trade owner onto what is saved now (`producerPricesDraft`), as the schedule
 * card merges. A delivery that shipped meanwhile turns the dialog read-only.
 */
const props = withDefaults(
  defineProps<{
    delivery: DeliveryResponse | null;
    /** `DealResponse.plan_version`, the stamp a save is composed against. */
    planVersion: number;
    readOnly?: boolean;
    busy?: boolean;
    errorMessage?: string | null;
  }>(),
  { readOnly: false, busy: false, errorMessage: null }
);

const emit = defineEmits<{
  submit: [request: ProducerPricesRequest];
}>();

const model = defineModel<boolean>({ required: true });

const { t } = useI18n();
const deliveryWindowText = useDeliveryWindowText();
const conflictText = usePriceConflictText();
const defaultPriceId = useId();

const forceToggle = ref<(value: boolean) => void>();

/**
 * `ModalComponent` hands its toggle over during its own setup, before any watcher has run, so the
 * current model value is applied at once — otherwise a modal mounted already open stays hidden.
 */
const setForceToggle = (toggle: (value: boolean) => void) => {
  forceToggle.value = toggle;
  toggle(model.value);
};

const draft = ref<ProducerPricesDraft>({});
/** The saved values the draft is composed against, and their `plan_version`. */
const base = ref<ProducerPricesDraft>({});
const baseVersion = ref(0);
const mergeConflicts = ref<Record<string, DraftConflict<ProducerPriceValue>>>(
  {}
);
const mergeNotice = ref<MergeNotice | null>(null);

const { options: candidateOptions, fetchCandidates } = useSwapReferrers();

const isReadOnly = computed(
  () =>
    props.readOnly ||
    props.delivery === null ||
    mergeNotice.value?.shipped === true
);

/** Someone else's save touched nothing in this dialog (another delivery, say): only Save again is needed. */
const mergeUnaffected = computed(
  () =>
    mergeNotice.value !== null &&
    mergeNotice.value.takenOver === 0 &&
    mergeNotice.value.conflicts === 0 &&
    mergeNotice.value.mandatesRemoved === 0
);

const hasMergeConflicts = computed(
  () => Object.keys(mergeConflicts.value).length > 0
);

const priceConflicts = computed<PriceConflict[]>(() =>
  props.delivery === null
    ? []
    : draftPriceConflicts(props.delivery, draft.value)
);

const savedPriceText = (price: string | null): string =>
  price === null
    ? t("forwards.deals.producer_prices.blank_price")
    : t("forwards.deals.plan.unit_price", { price });

/** A removed kept price leaves the draft, and with it the dialog; a backing mandate always stays. */
const rows = computed<MandateRow[]>(() => {
  const delivery = props.delivery;
  if (delivery === null) return [];
  const conflicted = new Set(
    priceConflicts.value
      .filter((conflict) => conflict.holds_shipment)
      .map((conflict) => conflict.company_id)
  );
  return delivery.mandate_prices
    .filter(
      (price) =>
        price.backs_delivery || mandateField(price.mandate_id) in draft.value
    )
    .map((price) => {
      const lines = delivery.producer_lines
        .filter((line) => line.mandate_ids.includes(price.mandate_id))
        .map((line) => ({
          companyId: line.company_id,
          companyName: line.company_name ?? String(line.company_id),
          amount: line.amount,
          invoicedAt: savedPriceText(line.unit_price),
          inConflict: conflicted.has(line.company_id),
        }));
      return {
        mandateId: price.mandate_id,
        mandateName: price.mandate_name,
        field: mandateField(price.mandate_id),
        savedPrice: price.unit_price,
        minimumPrice: price.minimum_price,
        backsDelivery: price.backs_delivery,
        amount: sumBy(lines, (line) => line.amount),
        lines,
      };
    });
});

const tradeOwnerCompanyId = computed({
  get: () => {
    const value = draft.value[TRADE_OWNER_FIELD];
    return typeof value === "number" ? value : null;
  },
  set: (companyId: number | null | undefined) => {
    draft.value[TRADE_OWNER_FIELD] = companyId ?? null;
  },
});

const windowText = computed(() =>
  props.delivery === null ? "" : deliveryWindowText(props.delivery)
);

const title = computed(() =>
  t("forwards.deals.producer_prices.title", { window: windowText.value })
);

/** The draft's default producer price, null while it is blank. */
const draftDefaultPrice = computed(() =>
  valueText(draft.value[DEFAULT_PRICE_FIELD])
);

/** What a blank mandate pays: the draft's default, else the delivery's own price. */
const fallbackPrice = computed(
  () => draftDefaultPrice.value ?? props.delivery?.unit_price ?? null
);

const deliveryPriceNote = computed(() => {
  if (draftDefaultPrice.value !== null)
    return t("forwards.deals.producer_prices.default_price_note", {
      price: t("forwards.deals.plan.unit_price", {
        price: draftDefaultPrice.value,
      }),
    });
  return t("forwards.deals.producer_prices.delivery_price_note", {
    price:
      props.delivery?.unit_price === null ||
      props.delivery?.unit_price === undefined
        ? t("forwards.deals.plan.no_price")
        : t("forwards.deals.plan.unit_price", {
            price: props.delivery.unit_price,
          }),
  });
});

const readOnlyDefaultPrice = computed(() => {
  const price = props.delivery?.default_producer_price ?? null;
  return price === null
    ? t("forwards.deals.producer_prices.default_price_none")
    : t("forwards.deals.plan.unit_price", { price });
});

/** The standing trade owner stays selectable even if it is no longer a candidate, so it is not lost silently. */
const tradeOwnerOptions = computed(() => {
  const current = props.delivery?.trade_owner;
  if (
    current === null ||
    current === undefined ||
    candidateOptions.value.some((option) => option.value === current.company_id)
  )
    return candidateOptions.value;
  return [
    ...candidateOptions.value,
    {
      value: current.company_id,
      label: current.company_name ?? `#${current.company_id}`,
    },
  ];
});

const readOnlyTradeOwner = computed(() => {
  const current = props.delivery?.trade_owner;
  if (current === null || current === undefined)
    return t("forwards.deals.producer_prices.trade_owner_none");
  return current.company_name ?? `#${current.company_id}`;
});

watch(model, (isOpen) => {
  forceToggle.value?.(isOpen);
  if (!isOpen) return;
  base.value =
    props.delivery === null ? {} : savedProducerPrices(props.delivery);
  draft.value = { ...base.value };
  baseVersion.value = props.planVersion;
  mergeConflicts.value = {};
  mergeNotice.value = null;
  if (!props.readOnly && candidateOptions.value.length === 0)
    void fetchCandidates();
});

const removeMandatePrice = (field: string) => {
  draft.value = omit(draft.value, field);
  mergeConflicts.value = omit(mergeConflicts.value, field);
};

const priceText = (value: ProducerPriceValue) =>
  valueText(value) ??
  (draftDefaultPrice.value === null
    ? t("forwards.deals.producer_prices.blank_price")
    : t("forwards.deals.producer_prices.blank_follows_default"));

const defaultPriceText = (value: ProducerPriceValue) =>
  valueText(value) ?? t("forwards.deals.producer_prices.blank_price");

const tradeOwnerText = (value: ProducerPriceValue) => {
  if (typeof value !== "number")
    return t("forwards.deals.producer_prices.trade_owner_none");
  return (
    tradeOwnerOptions.value.find((option) => option.value === value)?.label ??
    `#${value}`
  );
};

const pick = (field: string, side: "mine" | "theirs") => {
  const conflict = mergeConflicts.value[field];
  if (conflict === undefined) return;
  draft.value[field] = conflict[side];
  mergeConflicts.value = omit(mergeConflicts.value, field);
};

const close = () => {
  model.value = false;
};

const submit = () => {
  if (props.delivery === null || hasMergeConflicts.value) return;
  emit("submit", producerPricesRequest(draft.value, baseVersion.value));
};

/**
 * Merges the draft onto the delivery as the props now show it, after the parent reloaded the deal
 * for a stale save. What is saved now becomes the base, so the next Save retries against it.
 */
const mergeSavedPrices = () => {
  const latest = props.delivery;
  if (latest === null) {
    mergeNotice.value = {
      takenOver: 0,
      conflicts: 0,
      mandatesRemoved: 0,
      shipped: false,
    };
    return;
  }
  const merged = mergeProducerPrices(base.value, draft.value, latest);
  draft.value = merged.draft;
  mergeConflicts.value = merged.shipped ? {} : merged.conflicts;
  base.value = savedProducerPrices(latest);
  baseVersion.value = props.planVersion;
  mergeNotice.value = {
    takenOver: merged.takenOver,
    conflicts: Object.keys(mergeConflicts.value).length,
    mandatesRemoved: merged.mandatesRemoved,
    shipped: merged.shipped,
  };
};

defineExpose({ mergeSavedPrices });
</script>

<style scoped lang="scss">
.price-cell {
  width: 10rem;
}

.default-price-input {
  max-width: 10rem;
}
</style>
